// Bangkok (Asia/Bangkok, UTC+7, no DST) time helpers for the scheduling workspace.
//
// IMPLEMENTATION_PLAN.md Phase 2: "เวลาบนแกนเป็น Bangkok; รองรับข้ามวัน ไม่แสดงเพียง HH:mm จนสับสน"
// Timestamps from the API are ISO datetimes (naive values represent Bangkok wall time; offsets represent instants).
// We always render in Asia/Bangkok explicitly instead of relying on the browser's local timezone,
// since operators/planners on a machine set to a different timezone must still see plant-local time.

const BANGKOK_TZ = 'Asia/Bangkok';

export function toBangkokDate(value) {
  if (!value) return null;
  const normalized = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value) && !/(Z|[+-]\d{2}:?\d{2})$/i.test(value) ? `${value}+07:00` : value;
  const date = value instanceof Date ? value : new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

// Full "YYYY-MM-DD HH:mm" in Bangkok time — safe for cross-midnight display and sorting by string.
export function formatBangkokDateTime(value, { withSeconds = false } = {}) {
  const date = toBangkokDate(value);
  if (!date) return '—';
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: BANGKOK_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: withSeconds ? '2-digit' : undefined,
    hour12: false,
  });
  const parts = formatter.formatToParts(date).reduce((acc, part) => {
    acc[part.type] = part.value;
    return acc;
  }, {});
  const base = `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
  return withSeconds ? `${base}:${parts.second}` : base;
}

// Short "HH:mm" — only safe for same-day ranges. Callers must pair with formatBangkokDateTime
// or a day marker when a range can cross midnight (Gantt rows, batch tables).
export function formatBangkokTime(value) {
  const date = toBangkokDate(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: BANGKOK_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

export function formatBangkokDay(value) {
  const date = toBangkokDate(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: BANGKOK_TZ,
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  }).format(date);
}

// Renders a start/end pair, adding the day whenever the range crosses a Bangkok calendar day
// so a bar that runs 23:40 -> 00:35 doesn't silently read as "backwards" or same-minute.
export function formatBangkokRange(startValue, endValue) {
  const start = toBangkokDate(startValue);
  const end = toBangkokDate(endValue);
  if (!start || !end) return '—';
  const startDay = formatBangkokDay(start);
  const endDay = formatBangkokDay(end);
  if (startDay === endDay) {
    return `${startDay} ${formatBangkokTime(start)}–${formatBangkokTime(end)}`;
  }
  return `${startDay} ${formatBangkokTime(start)} → ${endDay} ${formatBangkokTime(end)}`;
}

// Converts a value already expressed in Bangkok wall-clock time (from a <input type="datetime-local">)
// into an ISO string with the +07:00 offset, so the backend receives an unambiguous instant
// regardless of the browser's own timezone.
export function bangkokLocalInputToIso(localValue) {
  if (!localValue) return null;
  // localValue looks like "2026-09-22T08:00"
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(localValue)) throw new Error('Invalid Bangkok datetime');
  return `${localValue.length === 16 ? `${localValue}:00` : localValue}+07:00`;
}

export function isoToBangkokLocalInput(isoValue) {
  const date = toBangkokDate(isoValue);
  if (!date) return '';
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: BANGKOK_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
  });
  const parts = formatter.formatToParts(date).reduce((acc, part) => {
    acc[part.type] = part.value;
    return acc;
  }, {});
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

export const BANGKOK_TIMEZONE = BANGKOK_TZ;
